/**
 * Utility function to parse exercise explanations that may be in JSON format
 * containing translations in multiple languages.
 */

/**
 * Parse an explanation that might be a JSON object with multiple language versions
 * or a plain text string.
 *
 * @param explanation - The explanation field from the exercise (may be JSON or plain text)
 * @param nativeLanguageIso - The user's native language ISO code (e.g., 'en-US', 'es-ES')
 * @returns The explanation in the user's native language, or a fallback
 */
export function parseExplanation(
  explanation: string | undefined,
  nativeLanguageIso: string
): string {
  // Handle undefined, null, or empty explanations
  if (!explanation) {
    return '';
  }

  // Try to parse as JSON
  try {
    const parsed = JSON.parse(explanation);

    // If it's not an object, return the original
    if (typeof parsed !== 'object' || parsed === null) {
      return explanation;
    }

    // Extract the base language code (e.g., 'en' from 'en-US')
    const baseLanguage = nativeLanguageIso.split('-')[0].toLowerCase();

    // Try to find explanation in native language (case-insensitive)
    const languageKeys = Object.keys(parsed);

    // First, try exact match with base language
    const exactMatch = languageKeys.find(
      key => key.toLowerCase() === baseLanguage
    );

    if (exactMatch && typeof parsed[exactMatch] === 'string') {
      return parsed[exactMatch];
    }

    // Fallback 1: Try English
    const englishKey = languageKeys.find(
      key => key.toLowerCase() === 'en'
    );

    if (englishKey && typeof parsed[englishKey] === 'string') {
      return parsed[englishKey];
    }

    // Fallback 2: Return first available string value
    for (const key of languageKeys) {
      if (typeof parsed[key] === 'string') {
        return parsed[key];
      }
    }

    // If no valid string found, return empty
    return '';
  } catch (error) {
    // If JSON parsing fails, it's a plain text explanation
    return explanation;
  }
}